import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import apiClient from "../api";

const Badge = ({ children, tone = "gray" }) => {
  const tones = {
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-green-100 text-green-700 border-green-200",
    yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    purple: "bg-purple-100 text-purple-700 border-purple-200",
    red: "bg-red-100 text-red-700 border-red-200",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
        tones[tone] || tones.gray
      }`}
    >
      {children}
    </span>
  );
};

const SectionCard = ({ title, subtitle, icon, children }) => (
  <div className="bg-white border rounded-2xl p-5 shadow-sm">
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-gray-900">
        {icon ? `${icon} ${title}` : title}
      </h2>
      {subtitle ? <p className="text-sm text-gray-500 mt-1">{subtitle}</p> : null}
    </div>
    {children}
  </div>
);

const InfoRow = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
    <span className="text-sm font-medium text-gray-600 min-w-[140px]">{label}</span>
    <span
      className={`text-sm text-gray-900 text-right break-all ${
        mono ? "font-mono text-[13px]" : ""
      }`}
    >
      {value ?? "-"}
    </span>
  </div>
);

const normalizeArray = (value) => (Array.isArray(value) ? value : []);
const asText = (value) => {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getObjectId = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value._id) return value._id;
  return "-";
};

const AdminBusView = () => {
  const { busId } = useParams();
  const navigate = useNavigate();

  const [bus, setBus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchBus = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await apiClient.get(`/buses/${busId}`);
        setBus(res.data);
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.message || "Failed to load bus details.");
      } finally {
        setLoading(false);
      }
    };

    fetchBus();
  }, [busId]);

  const boardingPoints = normalizeArray(bus?.boardingPoints);
  const droppingPoints = normalizeArray(bus?.droppingPoints);
  const fares = normalizeArray(bus?.fares);
  const features = normalizeArray(bus?.features);
  const unavailableDates = normalizeArray(bus?.unavailableDates);
  const routeStops = normalizeArray(bus?.routeStops);
  const tags = normalizeArray(bus?.tags);
  const gallery = normalizeArray(bus?.media?.gallery);
  const rotationIntervals = normalizeArray(bus?.rotationSchedule?.intervals);
  const viaStops = normalizeArray(bus?.routeMeta?.via);
  const seatLayout = normalizeArray(bus?.seatLayout);

  const operatorName =
    bus?.operator?.fullName ||
    bus?.operator?.name ||
    bus?.operatorName ||
    bus?.operator?.email ||
    "-";

  const operatorId = getObjectId(bus?.operator);
  const registrationNo =
    bus?.registrationNo || bus?.vehicle?.registrationNo || "-";

  const normalizedFacilities = useMemo(() => {
    if (!bus?.facilities) return [];
    if (Array.isArray(bus.facilities)) return bus.facilities;
    if (typeof bus.facilities === "object") {
      return Object.entries(bus.facilities)
        .filter(([, v]) => Boolean(v))
        .map(([k]) => k);
    }
    return [];
  }, [bus]);

  if (loading) {
    return <div className="p-6">Loading bus details...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  if (!bus) {
    return <div className="p-6">Bus not found.</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* HERO */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-2xl p-6 mb-6 shadow-sm">
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5">
          <div>
            <p className="text-sm text-indigo-100 mb-1">Bus Overview</p>
            <h1 className="text-2xl md:text-3xl font-bold">{bus.name || "-"}</h1>
            <p className="text-sm text-indigo-100 mt-2">
              Complete bus profile, pricing, route, schedule, and media details
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge tone={bus.isAvailable === false ? "red" : "green"}>
              {bus.isAvailable === false ? "Unavailable" : "Available"}
            </Badge>
            <Badge tone={bus.listingMode === "timetableOnly" ? "yellow" : "blue"}>
              {bus.listingMode === "timetableOnly" ? "Timetable Only" : "Booking Enabled"}
            </Badge>
            <Badge tone={bus.rotationSchedule?.isRotating ? "purple" : "gray"}>
              {bus.rotationSchedule?.isRotating ? "Rotating Schedule" : "Fixed Schedule"}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mt-6">
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="text-xs uppercase tracking-wide text-indigo-100">Route</p>
            <p className="text-base font-semibold mt-1">
              {bus.from || "-"} → {bus.to || "-"}
            </p>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="text-xs uppercase tracking-wide text-indigo-100">Schedule</p>
            <p className="text-base font-semibold mt-1">{bus.departureTime || "-"}</p>
            <p className="text-sm text-indigo-100 mt-1">Arrival {bus.arrivalTime || "-"}</p>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="text-xs uppercase tracking-wide text-indigo-100">Operator</p>
            <p className="text-base font-semibold mt-1 break-words">{operatorName}</p>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="text-xs uppercase tracking-wide text-indigo-100">Seats / Price</p>
            <p className="text-base font-semibold mt-1">{seatLayout.length || 0} seats</p>
            <p className="text-sm text-indigo-100 mt-1">
              Rs. {Number(bus.price || 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/admin/edit-bus/${bus._id}`}
            className="px-4 py-2 rounded-lg border bg-blue-50 text-blue-700 border-blue-200 text-sm font-medium hover:bg-blue-100"
          >
            Edit Bus
          </Link>
          <Link
            to="/admin/availability"
            className="px-4 py-2 rounded-lg border bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
          >
            View Availability
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="px-4 py-2 rounded-lg border bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
          >
            Print
          </button>
        </div>

        <button
          onClick={() => navigate("/admin/buses")}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm font-medium"
        >
          ← Back to Buses
        </button>
      </div>

      {/* GRID 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard
          title="Basic Bus Information"
          subtitle="Core identification and listing status"
          icon="🚌"
        >
          <InfoRow label="Bus Name" value={bus.name || "-"} />
          <InfoRow label="Bus Number" value={bus.busNumber || "-"} />
          <InfoRow label="Bus Type" value={bus.busType || "-"} />
          <InfoRow label="Listing Mode" value={bus.listingMode || "-"} />
          <InfoRow label="Available" value={bus.isAvailable === false ? "No" : "Yes"} />
          <InfoRow label="Show Public Contact" value={bus.showPublicContact ? "Yes" : "No"} />
          <InfoRow label="Seat Count" value={String(seatLayout.length || 0)} />
          <InfoRow label="Bus ID" value={bus._id || "-"} mono />
        </SectionCard>

        <SectionCard
          title="Operator & Contact Details"
          subtitle="Operator, conductor, owner, and notifications"
          icon="👤"
        >
          <InfoRow label="Operator Name" value={operatorName} />
          <InfoRow label="Operator ID" value={operatorId} mono />
          <InfoRow label="Operator Email" value={bus?.operator?.email || "-"} />
          <InfoRow label="Conductor Name" value={bus?.conductor?.name || "-"} />
          <InfoRow label="Conductor Mobile" value={bus?.conductor?.mobile || "-"} />
          <InfoRow label="Conductor Alt Mobile" value={bus?.conductor?.altMobile || "-"} />
          <InfoRow label="Owner Name" value={bus?.owner?.name || "-"} />
          <InfoRow label="Owner Notify Mobile" value={bus?.ownerNotifyMobile || "-"} />
        </SectionCard>

        <SectionCard
          title="Route & Schedule"
          subtitle="Main route and primary timing details"
          icon="🗺️"
        >
          <InfoRow label="From" value={bus.from || "-"} />
          <InfoRow label="To" value={bus.to || "-"} />
          <InfoRow label="Departure Time" value={bus.departureTime || "-"} />
          <InfoRow label="Arrival Time" value={bus.arrivalTime || "-"} />
          <InfoRow label="Date" value={bus.date || "-"} />
          <InfoRow label="Can Book" value={bus.canBook ? "Yes" : "No"} />
        </SectionCard>

        <SectionCard
          title="Pricing & Fee Details"
          subtitle="Default price, convenience fee, and booking pricing mode"
          icon="💳"
        >
          <InfoRow
            label="Base Price"
            value={`Rs. ${Number(bus.price || 0).toLocaleString()}`}
          />
          <InfoRow
            label="Convenience Fee Type"
            value={bus?.convenienceFee?.amountType || "-"}
          />
          <InfoRow
            label="Convenience Fee Value"
            value={
              bus?.convenienceFee?.value != null
                ? String(bus.convenienceFee.value)
                : "-"
            }
          />
          <InfoRow
            label="Trending Offer Active"
            value={bus?.trendingOffer?.isActive ? "Yes" : "No"}
          />
          <InfoRow
            label="Trending Offer Discount %"
            value={bus?.trendingOffer?.discountPercent ?? "-"}
          />
          <InfoRow
            label="Trending Offer Message"
            value={bus?.trendingOffer?.message || "-"}
          />
          <InfoRow
            label="Trending Offer Expiry"
            value={bus?.trendingOffer?.expiry || "-"}
          />
        </SectionCard>
      </div>

      {/* GRID 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard
          title="Vehicle Details"
          subtitle="Registration and vehicle metadata"
          icon="🚐"
        >
          <InfoRow label="Registration No" value={registrationNo} />
          <InfoRow label="Vehicle Registration" value={bus?.vehicle?.registrationNo || "-"} />
          <InfoRow label="Vehicle Make" value={bus?.vehicle?.make || "-"} />
          <InfoRow label="Vehicle Model" value={bus?.vehicle?.model || "-"} />
          <InfoRow label="Vehicle Year" value={bus?.vehicle?.year || "-"} />
          <InfoRow label="Vehicle Color" value={bus?.vehicle?.color || "-"} />
        </SectionCard>

        <SectionCard
          title="Features & Facilities"
          subtitle="On-board offerings and tags"
          icon="✨"
        >
          <InfoRow
            label="Features"
            value={features.length ? features.join(", ") : "-"}
          />
          <InfoRow
            label="Facilities"
            value={normalizedFacilities.length ? normalizedFacilities.join(", ") : "-"}
          />
          <InfoRow label="Tags" value={tags.length ? tags.join(", ") : "-"} />
        </SectionCard>
      </div>

      {/* ROUTE STOPS / META */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard
          title="Route Stops"
          subtitle="Minimal route stop list used for cards and previews"
          icon="📍"
        >
          {routeStops.length ? (
            <div className="flex flex-wrap gap-2">
              {routeStops.map((stop, index) => (
                <Badge key={`${stop}-${index}`} tone="blue">
                  {stop}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No route stops available.</p>
          )}
        </SectionCard>

        <SectionCard
          title="Route Meta"
          subtitle="Extended route metadata and via points"
          icon="🧭"
        >
          <InfoRow label="Distance" value={bus?.routeMeta?.distance || "-"} />
          <InfoRow label="Duration" value={bus?.routeMeta?.duration || "-"} />
          <InfoRow label="Via Stops" value={viaStops.length ? viaStops.join(", ") : "-"} />
        </SectionCard>
      </div>

      {/* BOARDING / DROPPING */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard
          title="Boarding Points"
          subtitle="All configured boarding locations"
          icon="🪧"
        >
          {boardingPoints.length ? (
            <div className="overflow-auto rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border-b px-3 py-3 text-left">Point</th>
                    <th className="border-b px-3 py-3 text-left">City</th>
                    <th className="border-b px-3 py-3 text-left">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {boardingPoints.map((p, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="border-b px-3 py-3">{p.point || "-"}</td>
                      <td className="border-b px-3 py-3">{p.city || "-"}</td>
                      <td className="border-b px-3 py-3">{p.time || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No boarding points available.</p>
          )}
        </SectionCard>

        <SectionCard
          title="Dropping Points"
          subtitle="All configured dropping locations"
          icon="🏁"
        >
          {droppingPoints.length ? (
            <div className="overflow-auto rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border-b px-3 py-3 text-left">Point</th>
                    <th className="border-b px-3 py-3 text-left">City</th>
                    <th className="border-b px-3 py-3 text-left">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {droppingPoints.map((p, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="border-b px-3 py-3">{p.point || "-"}</td>
                      <td className="border-b px-3 py-3">{p.city || "-"}</td>
                      <td className="border-b px-3 py-3">{p.time || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No dropping points available.</p>
          )}
        </SectionCard>
      </div>

      {/* FARES */}
      <div className="mt-6">
        <SectionCard
          title="Fare Details"
          subtitle="Point-to-point fare matrix for this bus"
          icon="💰"
        >
          {fares.length ? (
            <div className="overflow-auto rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border-b px-3 py-3 text-left">Boarding Point</th>
                    <th className="border-b px-3 py-3 text-left">Dropping Point</th>
                    <th className="border-b px-3 py-3 text-left">Fare</th>
                  </tr>
                </thead>
                <tbody>
                  {fares.map((fare, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="border-b px-3 py-3">{fare.boardingPoint || "-"}</td>
                      <td className="border-b px-3 py-3">{fare.droppingPoint || "-"}</td>
                      <td className="border-b px-3 py-3">
                        Rs. {Number(fare.price || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No fare details available.</p>
          )}
        </SectionCard>
      </div>

      {/* UNAVAILABLE DATES / SEAT LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard
          title="Unavailable Dates"
          subtitle="Dates on which this bus is not available"
          icon="📅"
        >
          {unavailableDates.length ? (
            <div className="flex flex-wrap gap-2">
              {unavailableDates.map((date, index) => (
                <Badge key={`${date}-${index}`} tone="red">
                  {date}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No unavailable dates configured.</p>
          )}
        </SectionCard>

        <SectionCard
          title="Seat Layout"
          subtitle="Stored seat layout values"
          icon="💺"
        >
          {seatLayout.length ? (
            <div className="flex flex-wrap gap-2">
              {seatLayout.map((seat, index) => (
                <Badge key={`${seat}-${index}`} tone="gray">
                  {seat}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No seat layout available.</p>
          )}
        </SectionCard>
      </div>

      {/* ROTATION */}
      <div className="mt-6">
        <SectionCard
          title="Rotation Schedule"
          subtitle="Advanced rotating schedule, day offsets, and per-turn timing"
          icon="🔁"
        >
          <InfoRow
            label="Is Rotating"
            value={bus?.rotationSchedule?.isRotating ? "Yes" : "No"}
          />
          <InfoRow label="Start Date" value={bus?.rotationSchedule?.startDate || "-"} />
          <InfoRow
            label="Rotation Length"
            value={
              bus?.rotationSchedule?.rotationLength != null
                ? String(bus.rotationSchedule.rotationLength)
                : "-"
            }
          />

          {rotationIntervals.length ? (
            <div className="mt-4 space-y-4">
              {rotationIntervals.map((interval, idx) => (
                <div key={idx} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h3 className="font-semibold text-gray-900">
                      Day Offset {interval.dayOffset ?? "-"}
                    </h3>
                    <Badge tone="purple">
                      {normalizeArray(interval.turns).length} turn(s)
                    </Badge>
                  </div>

                  {normalizeArray(interval.turns).length ? (
                    <div className="space-y-4">
                      {interval.turns.map((turn, turnIndex) => (
                        <div
                          key={turnIndex}
                          className="rounded-lg border border-gray-100 bg-gray-50 p-4"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                Departure: {turn.departureTime || "-"}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                Arrival: {turn.arrivalTime || "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                Boarding Points: {normalizeArray(turn.boardingPoints).length}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                Dropping Points: {normalizeArray(turn.droppingPoints).length}
                              </p>
                            </div>
                          </div>

                          {normalizeArray(turn.boardingPoints).length ? (
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                Boarding Points
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {turn.boardingPoints.map((p, i) => (
                                  <Badge key={i} tone="blue">
                                    {p.point || "-"} {p.time ? `(${p.time})` : ""}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ) : null}

                          {normalizeArray(turn.droppingPoints).length ? (
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                Dropping Points
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {turn.droppingPoints.map((p, i) => (
                                  <Badge key={i} tone="gray">
                                    {p.point || "-"} {p.time ? `(${p.time})` : ""}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No turns configured for this interval.</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 mt-3">No rotation intervals available.</p>
          )}
        </SectionCard>
      </div>

      {/* MEDIA */}
      <div className="mt-6">
        <SectionCard
          title="Media & Branding"
          subtitle="Cover image, gallery, and operator branding assets"
          icon="🖼️"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Cover Image</p>
              {bus?.media?.cover?.url ? (
                <img
                  src={bus.media.cover.url}
                  alt="Bus cover"
                  className="w-full h-60 object-cover rounded-xl border"
                />
              ) : (
                <div className="h-60 rounded-xl border bg-gray-50 flex items-center justify-center text-sm text-gray-400">
                  No cover image
                </div>
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Operator Logo</p>
              {bus?.operatorLogo ? (
                <img
                  src={bus.operatorLogo}
                  alt="Operator logo"
                  className="w-full max-w-xs h-40 object-contain rounded-xl border bg-white p-4"
                />
              ) : (
                <div className="h-40 rounded-xl border bg-gray-50 flex items-center justify-center text-sm text-gray-400">
                  No operator logo
                </div>
              )}
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Gallery</p>
            {gallery.length ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {gallery.map((img, index) => (
                  <div key={index} className="rounded-xl border overflow-hidden bg-white">
                    <img
                      src={img.url}
                      alt={img.caption || `Gallery ${index + 1}`}
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-3">
                      <p className="text-xs text-gray-600">
                        {img.caption || "No caption"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No gallery images available.</p>
            )}
          </div>
        </SectionCard>
      </div>

      {/* DETAILS / HTML / EXTRA */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard
          title="Text Details"
          subtitle="Human-readable bus description"
          icon="📝"
        >
          <div className="text-sm text-gray-700 whitespace-pre-wrap">
            {bus.details || "No detailed text available."}
          </div>
        </SectionCard>

        <SectionCard
          title="Extra Metadata"
          subtitle="Additional stored metadata for this bus"
          icon="📦"
        >
          <pre className="text-xs bg-gray-50 border rounded-xl p-4 overflow-auto text-gray-700">
            {JSON.stringify(bus.extra || {}, null, 2)}
          </pre>
        </SectionCard>
      </div>

      {/* RAW IDENTIFIERS */}
      <div className="mt-6">
        <SectionCard
          title="Reference & Identifiers"
          subtitle="Useful IDs and raw metadata for debugging"
          icon="🔎"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <InfoRow label="Bus ID" value={bus._id || "-"} mono />
              <InfoRow label="Operator ID" value={operatorId} mono />
              <InfoRow label="Registration No" value={registrationNo} mono />
            </div>
            <div>
              <InfoRow label="Cover Public ID" value={bus?.media?.cover?.publicId || "-"} mono />
              <InfoRow label="Listing Mode" value={bus.listingMode || "-"} />
              <InfoRow label="Can Book" value={bus.canBook ? "Yes" : "No"} />
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default AdminBusView;
